import { Component, computed, effect, EventEmitter, inject, input, Output, Signal, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { debounceTime, finalize, map, of, startWith } from 'rxjs';
import { ButtonModule } from '@openng/optimus-ui/button';
import {
  CurrencyService,
  CurrencyInputFieldComponent,
  CurrencyFormatPipe,
  DropdownData,
  GenericDropdownComponent,
  rxResourceFixed
} from '@zayan-erp/shared';
import { RadioButtonModule } from '@openng/optimus-ui/radiobutton';
import { DsmLotIncentiveService } from '../../../services/dsm-lot-incentive.service';
import { DsmDealOptionsService } from '../../../services/dsm-deal-options.service';
import { LotAddendumIncentiveApplicationMethod } from '../../../enums/dsm-asp-incentives.enums';
import { DsmAddendumIncentivePreviewComponent } from '../dsm-addendum-incentive-preview/dsm-addendum-incentive-preview.component';
import { DsmApplyAddendumIncentivePayload } from '../../../models/dsm-lot-incentives.models';

@Component({
  selector: 'dsm-incentive-reallocation-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CurrencyInputFieldComponent,
    ButtonModule,
    RadioButtonModule,
    CurrencyFormatPipe,
    GenericDropdownComponent,
    DsmAddendumIncentivePreviewComponent
  ],
  templateUrl: './dsm-incentive-reallocation-form.component.html'
})
export class DsmIncentiveReallocationFormComponent {
  private readonly fb = inject(FormBuilder);
  private readonly currencyService = inject(CurrencyService);
  private readonly lotIncentiveService = inject(DsmLotIncentiveService);
  private readonly dealOptionsService = inject(DsmDealOptionsService);

  readonly dealId = input.required<string>();
  /** Amount freed by the cancelled addendum — used as the initial value of the amount field. */
  readonly freedAmount = input<number>(0);
  /** Optional human-readable label for the cancelled addendum (e.g. "ADMI1001 line 1"). */
  readonly sourceAddendumLabel = input<string | null>(null);

  readonly poolTotal = this.lotIncentiveService.incentiveTotal;
  readonly applied = this.lotIncentiveService.incentiveApplied;
  readonly maxApplicable = this.lotIncentiveService.incentiveRemaining;

  readonly currencyCode = this.currencyService.currencyCode;
  readonly currencySymbol = this.currencyService.currencySymbol;

  readonly methodOptions: { label: string; value: LotAddendumIncentiveApplicationMethod; description: string }[] = [
    {
      label: 'Highest cost first',
      value: LotAddendumIncentiveApplicationMethod['Highest cost first'],
      description: 'Fills the most expensive option first until the amount runs out.'
    },
    {
      label: 'Order added',
      value: LotAddendumIncentiveApplicationMethod['Order added'],
      description: 'Fills options in the order they were added to the addendum.'
    }
  ];

  @Output() formSaved = new EventEmitter<void>();
  @Output() formDismiss = new EventEmitter<void>();

  form: FormGroup;
  readonly isSubmitting = signal(false);

  /** Available addendums to reallocate into — already excludes cancelled ones server-side. */
  private readonly addendumsResource = rxResourceFixed<DropdownData[], { dealId: string }>({
    params: () => ({ dealId: this.dealId() }),
    stream: ({ params }) =>
      params.dealId ? this.dealOptionsService.getDealAddendumsForDropdown(params.dealId) : of([])
  });
  readonly addendums = computed<DropdownData[]>(() => this.addendumsResource.value() ?? []);
  readonly isLoadingAddendums = computed(() => this.addendumsResource.isLoading());

  /**
   * Debounced form value used to drive the preview API call so we don't fire
   * a request on every keystroke / radio toggle / dropdown change.
   */
  private readonly debouncedFormValue: Signal<{
    amount: number | null;
    method: LotAddendumIncentiveApplicationMethod;
    targetAddendumId: string | null;
  }>;
  readonly previewAmount = computed<number | null>(() => {
    const amount = this.debouncedFormValue()?.amount ?? null;
    const max = this.maxApplicable();
    if (amount === null || amount === undefined || amount <= 0 || amount > max) {
      return null;
    }
    return amount;
  });
  readonly previewMethod = computed<LotAddendumIncentiveApplicationMethod>(
    () => this.debouncedFormValue()?.method ?? LotAddendumIncentiveApplicationMethod['Highest cost first']
  );
  readonly previewTargetAddendumId = computed<string | null>(() => this.debouncedFormValue()?.targetAddendumId ?? null);

  readonly formAmount: Signal<number | null>;
  readonly isFormInvalid: Signal<boolean>;
  readonly isSubmitDisabled = computed(() => this.isFormInvalid() || this.isSubmitting());
  readonly submitButtonLabel = computed(() => {
    const amount = this.formAmount();
    if (amount != null && amount > 0) {
      return `Reallocate ${this.currencyService.formatCurrency(amount)}`;
    }
    return 'Reallocate';
  });

  constructor() {
    this.form = this.fb.group({
      targetAddendumId: [null as string | null, [Validators.required]],
      amount: [null as number | null, [Validators.required, Validators.min(0.01)]],
      method: [LotAddendumIncentiveApplicationMethod['Highest cost first'], [Validators.required]]
    });

    this.formAmount = toSignal(
      this.form.valueChanges.pipe(
        startWith(this.form.value),
        map(v => v.amount as number | null)
      ),
      { initialValue: null as number | null }
    );
    this.isFormInvalid = toSignal(
      this.form.statusChanges.pipe(
        startWith(this.form.status),
        map(status => status !== 'VALID')
      ),
      { initialValue: true }
    );

    this.debouncedFormValue = toSignal(this.form.valueChanges.pipe(startWith(this.form.value), debounceTime(400)), {
      initialValue: this.form.value
    });

    // Sync the amount control with the freedAmount input until the user edits it.
    effect(() => {
      const freed = this.freedAmount();
      const amountControl = this.form.get('amount');
      if (freed > 0 && amountControl && !amountControl.dirty) {
        amountControl.setValue(freed, { emitEvent: false });
        amountControl.updateValueAndValidity();
      }
    });

    // Keep the max validator in sync with the pool remaining.
    effect(() => {
      const max = this.maxApplicable();
      this.form.get('amount')?.setValidators([Validators.required, Validators.min(0.01), Validators.max(max)]);
      this.form.get('amount')?.updateValueAndValidity({ emitEvent: false });
    });
  }

  setMaxAmount(): void {
    this.form.get('amount')?.setValue(this.maxApplicable());
    this.form.get('amount')?.markAsTouched();
    this.form.get('amount')?.markAsDirty();
  }

  submit(): void {
    if (this.form.invalid || this.isSubmitting()) {
      this.form.markAllAsTouched();
      return;
    }

    const val = this.form.getRawValue();
    const targetAddendumId = val.targetAddendumId as string | null;
    if (!targetAddendumId) return;

    const payload: DsmApplyAddendumIncentivePayload = {
      amount: val.amount as number,
      method: val.method as LotAddendumIncentiveApplicationMethod
    };

    this.isSubmitting.set(true);
    this.lotIncentiveService
      .applyIncentive(targetAddendumId, payload)
      .pipe(finalize(() => this.isSubmitting.set(false)))
      .subscribe({
        next: () => {
          this.formSaved.emit();
        }
      });
  }

  dismiss(): void {
    this.formDismiss.emit();
  }
}
